import React, { useState, useRef } from 'react';
import { runSetupWizard } from '../../api';
import { fc, calcPayoff, sourcesToMonthly, FREQ_LABELS } from '../../utils';

const FILE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const MAX_FILES = 3;
const MAX_FILE_BYTES = 4 * 1024 * 1024;
const FREQS = ['weekly', 'biweekly', 'semimonthly', 'monthly'];

// Read a File as base64 (without the data: prefix)
function readBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

// Turn AI income sources into the app's income source shape
function toIncomeSources(list, now) {
  return (list || [])
    .filter((x) => x && parseFloat(x.amt) > 0)
    .map((x, i) => {
      const freq = FREQS.includes(x.freq) ? x.freq : 'monthly';
      const payMs = x.payDate ? new Date(x.payDate + 'T12:00:00').getTime() : NaN;
      return {
        id: 'inc_' + (now + i), name: x.name || 'Paycheck', type: 'job', amt: parseFloat(x.amt), freq,
        ...(Number.isFinite(payMs) && ['weekly', 'biweekly'].includes(freq) ? { firstPayMs: payMs } : {}),
      };
    });
}

const SETUP_EXAMPLE = `I pay $1,450 rent on the 1st, $320 car payment on the 8th, $180 car insurance on the 12th, and electric around $90 on the 20th. I have Netflix $18 and Spotify $11 that auto-charge on the 15th. I also have a Chase Visa with $2,400 balance, 22% APR, $50 minimum. I get paid $2,800 take-home every two weeks.`;

const EDIT_EXAMPLE = `Remove Netflix, change my rent to $1,550, add Hulu $18 on the 15th, and update my Chase balance to $1,800.`;

function ItemChip({ label, sub, col, tag, tagCol, onRemove }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 10px', background: 'var(--surf2)',
      border: '1px solid var(--bdr)', borderLeft: `3px solid ${col || 'var(--bdr)'}`,
      borderRadius: 9, marginBottom: 6,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)' }}>{label}</span>
          {tag && (
            <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 99, background: tagCol + '22', color: tagCol, textTransform: 'uppercase', letterSpacing: '.3px' }}>
              {tag}
            </span>
          )}
        </div>
        {sub && <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>{sub}</div>}
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          style={{ flexShrink: 0, background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}
        >×</button>
      )}
    </div>
  );
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Match new items to existing ones by name (case-insensitive fuzzy match)
function matchByName(newItem, existing) {
  const n = newItem.name.toLowerCase().trim();
  return existing.find((e) => e.name.toLowerCase().trim() === n);
}

// Compute diff between old and new arrays
function computeDiff(oldArr, newArr) {
  const added = [], updated = [], removed = [], unchanged = [];
  (newArr || []).forEach((item) => {
    const match = matchByName(item, oldArr || []);
    if (!match) {
      added.push(item);
    } else {
      const changed = JSON.stringify({ amt: item.amt, due: item.due, col: item.col, cycle: item.cycle, bal: item.bal, apr: item.apr, min: item.min })
        !== JSON.stringify({ amt: match.amt, due: match.due, col: match.col, cycle: match.cycle, bal: match.bal, apr: match.apr, min: match.min });
      if (changed) updated.push({ ...item, _oldAmt: match.amt, _id: match.id });
      else unchanged.push({ ...item, _id: match.id });
    }
  });
  (oldArr || []).forEach((old) => {
    if (!matchByName(old, newArr || [])) removed.push(old);
  });
  return { added, updated, removed, unchanged };
}

export function SetupWizardModal({ onClose, updateState, showToast, state, mode = 'setup' }) {
  const isEdit = mode === 'edit';
  const [step, setStep] = useState('input');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Diffs per category
  const [billsDiff,  setBillsDiff]  = useState(null);
  const [subsDiff,   setSubsDiff]   = useState(null);
  const [cardsDiff,  setCardsDiff]  = useState(null);
  const [income,     setIncome]     = useState(0);

  // For non-edit mode: items the user can remove from preview
  const [bills,  setBills]  = useState([]);
  const [subs,   setSubs]   = useState([]);
  const [cards,  setCards]  = useState([]);
  const [incomeSrcs, setIncomeSrcs] = useState([]);
  const [files, setFiles] = useState([]); // { name, mimeType, content }
  const [summary, setSummary] = useState(null);
  const fileRef = useRef(null);

  const addFiles = async (list) => {
    setError(null);
    const picked = Array.from(list || []);
    if (files.length + picked.length > MAX_FILES) { setError(`Attach up to ${MAX_FILES} files at a time`); return; }
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) { setError(`${tooBig.name} is over 4 MB. Try a smaller photo or a screenshot.`); return; }
    try {
      const read = await Promise.all(picked.map(async (f) => ({ name: f.name, mimeType: f.type, content: await readBase64(f) })));
      setFiles((p) => [...p, ...read]);
    } catch {
      setError('Could not read that file. Try another one.');
    }
  };
  // Income sources only fill in when the account has none yet
  const canSetSources = !(state.incomeSources || []).length;

  const handleParse = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !files.length) || loading) return;
    setLoading(true);
    setError(null);

    const existing = isEdit ? {
      fixedBills: state.fixedBills || [],
      subscriptions: state.subscriptions || [],
      cards: state.cards || [],
      monthlyIncome: state.monthlyIncome || 0,
    } : null;

    const res = await runSetupWizard(trimmed, existing, files.map(({ mimeType, content }) => ({ mimeType, content })));
    setLoading(false);

    if (res.error) { setError(res.error); return; }

    const s = res.setup || {};
    setIncomeSrcs(canSetSources ? toIncomeSources(s.incomeSources, Date.now()) : []);

    if (isEdit) {
      setBillsDiff(computeDiff(state.fixedBills || [], s.fixedBills || []));
      setSubsDiff(computeDiff(state.subscriptions || [], s.subscriptions || []));
      setCardsDiff(computeDiff(state.cards || [], s.cards || []));
      setIncome(s.monthlyIncome || 0);
    } else {
      setBills((s.fixedBills || []).map((b, i) => ({ ...b, _id: 'fw_fb_' + Date.now() + i })));
      setSubs((s.subscriptions || []).map((b, i) => ({ ...b, _id: 'fw_sub_' + Date.now() + i })));
      setCards((s.cards || []).map((c, i) => ({ ...c, _id: 'fw_card_' + Date.now() + i })));
      setIncome(s.monthlyIncome || 0);
    }
    setStep('preview');
  };

  const handleApply = () => {
    const now = Date.now();
    const update = {};

    if (isEdit) {
      // Rebuild each array: keep unchanged (with original IDs), apply updates, add new, omit removed
      const rebuildBills = (diffs, oldArr, makeItem) => {
        if (!diffs) return null;
        const { added, updated, unchanged } = diffs;
        const result = [
          ...unchanged.map((x) => oldArr.find((o) => o.id === x._id) || x),
          ...updated.map((x) => {
            const orig = oldArr.find((o) => o.id === x._id);
            return orig ? { ...orig, ...makeItem(x) } : makeItem(x);
          }),
          ...added.map((x, i) => ({ id: 'fb_' + (now + i), ...makeItem(x) })),
        ];
        return result;
      };

      if (billsDiff) {
        update.fixedBills = rebuildBills(billsDiff, state.fixedBills || [], (b) => ({
          name: b.name, amt: b.amt || 0, due: b.due || 1, col: b.col || '#adb5bd',
        }));
      }
      if (subsDiff) {
        update.subscriptions = rebuildBills(subsDiff, state.subscriptions || [], (s) => ({
          name: s.name, amt: s.amt || 0, due: s.due || 1, cycle: s.cycle || 'monthly', col: s.col || '#adb5bd',
        }));
      }
      if (cardsDiff) {
        update.cards = rebuildBills(cardsDiff, state.cards || [], (c) => ({
          name: c.name, bal: c.bal || 0, apr: c.apr || 0, min: c.min || 0, col: c.col || '#ffd93d',
        }));
      }
      if (incomeSrcs.length) {
        update.incomeSources = incomeSrcs;
        update.monthlyIncome = Math.round(sourcesToMonthly(incomeSrcs) * 100) / 100;
      } else if (income > 0 && !state.monthlyIncome) update.monthlyIncome = income;
    } else {
      const newBills = bills.map((b, i) => ({ id: 'fb_' + (now + i), name: b.name, amt: b.amt || 0, due: b.due || 1, col: b.col || '#adb5bd' }));
      const newSubs = subs.map((b, i) => ({ id: 'sub_' + (now + 1000 + i), name: b.name, amt: b.amt || 0, due: b.due || 1, cycle: b.cycle || 'monthly', col: b.col || '#adb5bd' }));
      const newCards = cards.map((c, i) => ({ id: 'card_' + (now + 2000 + i), name: c.name, bal: c.bal || 0, apr: c.apr || 0, min: c.min || 0, col: c.col || '#ffd93d' }));
      if (newBills.length) update.fixedBills = [...(state.fixedBills || []), ...newBills];
      if (newSubs.length) update.subscriptions = [...(state.subscriptions || []), ...newSubs];
      if (newCards.length) update.cards = [...(state.cards || []), ...newCards];
      if (incomeSrcs.length) {
        update.incomeSources = incomeSrcs;
        update.monthlyIncome = Math.round(sourcesToMonthly(incomeSrcs) * 100) / 100;
      } else if (income > 0 && !state.monthlyIncome) update.monthlyIncome = income;
    }

    if (Object.keys(update).length) updateState(update);

    const msgs = [];
    if (isEdit) {
      const added = (billsDiff?.added.length || 0) + (subsDiff?.added.length || 0) + (cardsDiff?.added.length || 0);
      const removed = (billsDiff?.removed.length || 0) + (subsDiff?.removed.length || 0) + (cardsDiff?.removed.length || 0);
      const updated = (billsDiff?.updated.length || 0) + (subsDiff?.updated.length || 0) + (cardsDiff?.updated.length || 0);
      if (added) msgs.push(`${added} added`);
      if (updated) msgs.push(`${updated} updated`);
      if (removed) msgs.push(`${removed} removed`);
    } else {
      const n = bills.length + subs.length + cards.length;
      if (n) msgs.push(`${n} item${n !== 1 ? 's' : ''} added`);
    }

    showToast?.('✅ ' + (msgs.length ? msgs.join(', ') : 'No changes') + ' — setup updated');
    if (isEdit) { onClose(); return; }

    // First-time setup: show what the plan looks like right away
    const next = { ...state, ...update };
    const monthly = next.incomeSources?.length ? sourcesToMonthly(next.incomeSources) : next.monthlyIncome || 0;
    const billsMo = (next.fixedBills || []).reduce((t, b) => t + (+b.amt || 0), 0)
      + (next.subscriptions || []).reduce((t, x) => t + (x.cycle === 'annual' ? (+x.amt || 0) / 12 : +x.amt || 0), 0);
    const minsMo = (next.cards || []).reduce((t, c) => t + (+c.min || 0), 0);
    const debtCards = (next.cards || []).filter((c) => (+c.bal || 0) > 0 && (+c.min || 0) > 0);
    const left = monthly - billsMo - minsMo;
    const payoff = debtCards.length ? calcPayoff(debtCards, Math.max(0, Math.round(left * 0.2)), 'avalanche') : null;
    setSummary({ monthly, billsMo, minsMo, left, debt: debtCards.reduce((t, c) => t + (+c.bal || 0), 0), payoff });
    setStep('done');
  };

  // Count of meaningful changes
  const hasChanges = isEdit
    ? (billsDiff && (billsDiff.added.length + billsDiff.updated.length + billsDiff.removed.length > 0)) ||
      (subsDiff && (subsDiff.added.length + subsDiff.updated.length + subsDiff.removed.length > 0)) ||
      (cardsDiff && (cardsDiff.added.length + cardsDiff.updated.length + cardsDiff.removed.length > 0)) ||
      (income > 0 && !state.monthlyIncome) || incomeSrcs.length > 0
    : bills.length + subs.length + cards.length + (income > 0 ? 1 : 0) + incomeSrcs.length > 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 1100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: '20px 20px 0 0',
        padding: 24, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 -8px 40px rgba(0,0,0,.4)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--txt)', marginBottom: 3 }}>
              {isEdit ? '✏️ Edit Setup with AI' : '✨ Set up with AI'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.5 }}>
              {step === 'done'
                ? 'Here is your starting plan. You can change any of it later.'
                : step === 'input'
                ? isEdit
                  ? 'Describe what to change — add, remove, or update any bill, subscription, card, or income.'
                  : 'Snap a pay stub, a bill or a bank statement, or just describe your bills, income and cards. AI builds your setup.'
                : isEdit
                  ? 'Review the changes below, then apply.'
                  : `Found items — remove anything wrong, then apply.`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1, marginLeft: 12, flexShrink: 0 }}>×</button>
        </div>

        {/* ── Input step ── */}
        {step === 'input' && (
          <>
            {isEdit && (state.fixedBills?.length > 0 || state.subscriptions?.length > 0) && (
              <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 10, fontSize: 11, color: 'var(--mut)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--txt)' }}>Current:</strong>{' '}
                {[...(state.fixedBills || []), ...(state.subscriptions || [])].map((b) => b.name).join(', ')}
                {(state.cards || []).length > 0 && ', ' + state.cards.map((c) => c.name).join(', ')}
              </div>
            )}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={isEdit ? EDIT_EXAMPLE : SETUP_EXAMPLE}
              rows={5}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px',
                background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 12,
                color: 'var(--txt)', fontSize: 13, fontFamily: 'inherit',
                lineHeight: 1.6, resize: 'vertical', outline: 'none', marginBottom: 10,
              }}
            />
            <input ref={fileRef} type="file" accept={FILE_ACCEPT} multiple hidden
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={files.length >= MAX_FILES}
              style={{ width: '100%', padding: '10px', borderRadius: 12, marginBottom: 8, background: 'none', border: '1px dashed var(--bdr)', color: 'var(--txt)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              📎 Add a pay stub, bill or statement (photo or PDF)
            </button>
            {files.map((f, i) => (
              <ItemChip key={f.name + i} label={f.name} sub={f.mimeType === 'application/pdf' ? 'PDF' : 'Photo'}
                onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} />
            ))}
            <div style={{ fontSize: 10, color: 'var(--mut)', margin: '2px 0 10px', lineHeight: 1.5 }}>
              🔒 Files are only read to fill in your setup. They are not saved.
            </div>
            {error && (
              <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10, padding: '8px 12px', background: 'rgba(255,107,107,.08)', borderRadius: 8, border: '1px solid rgba(255,107,107,.2)' }}>
                ⚠️ {error}
              </div>
            )}
            <button
              onClick={handleParse}
              disabled={loading || (!text.trim() && !files.length)}
              style={{
                width: '100%', padding: '13px', borderRadius: 12,
                background: (text.trim() || files.length) && !loading ? 'var(--ylw)' : 'var(--surf2)',
                color: (text.trim() || files.length) && !loading ? '#000' : 'var(--mut)',
                fontWeight: 800, fontSize: 14, border: 'none',
                cursor: (text.trim() || files.length) && !loading ? 'pointer' : 'default',
                fontFamily: 'inherit', transition: 'background .15s',
              }}
            >
              {loading ? (files.length ? 'Reading your documents…' : 'Processing…') : isEdit ? 'Preview changes →' : 'Parse my setup →'}
            </button>
          </>
        )}

        {/* ── Preview step ── */}
        {step === 'preview' && (
          <>
            {isEdit ? (
              // Edit mode: show diff
              <>
                {[
                  { label: '📋 Bills', diff: billsDiff, type: 'bill' },
                  { label: '🔄 Subscriptions', diff: subsDiff, type: 'sub' },
                  { label: '💳 Cards', diff: cardsDiff, type: 'card' },
                ].map(({ label, diff }) => {
                  if (!diff) return null;
                  const { added, updated, removed } = diff;
                  if (!added.length && !updated.length && !removed.length) return null;
                  return (
                    <div key={label} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                        {label}
                      </div>
                      {added.map((b, i) => (
                        <ItemChip key={'a' + i} label={b.name}
                          sub={b.bal !== undefined ? `${fc(b.bal)} balance · ${b.apr}% APR` : `${fc(b.amt)} · due ${ordinal(b.due || 1)}`}
                          col={b.col || 'var(--grn)'} tag="new" tagCol="var(--grn)" />
                      ))}
                      {updated.map((b, i) => (
                        <ItemChip key={'u' + i} label={b.name}
                          sub={b.bal !== undefined ? `${fc(b.bal)} balance · ${b.apr}% APR` : `${fc(b.amt)} · due ${ordinal(b.due || 1)}${b._oldAmt !== b.amt ? ` (was ${fc(b._oldAmt)})` : ''}`}
                          col={b.col || 'var(--ylw)'} tag="updated" tagCol="var(--ylw)" />
                      ))}
                      {removed.map((b, i) => (
                        <ItemChip key={'r' + i} label={b.name}
                          sub={b.bal !== undefined ? `${fc(b.bal)} balance` : `${fc(b.amt)} · will be removed`}
                          col="var(--red)" tag="removed" tagCol="var(--red)" />
                      ))}
                    </div>
                  );
                })}
                {incomeSrcs.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>💰 Paychecks</div>
                    {incomeSrcs.map((x, i) => (
                      <ItemChip key={x.id} label={`${x.name}: ${fc(x.amt)} take-home`}
                        sub={`${FREQ_LABELS[x.freq] || x.freq}${x.firstPayMs ? ' · paid ' + new Date(x.firstPayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} · ${fc(sourcesToMonthly([x]))}/mo`}
                        col="var(--grn)" tag="new" tagCol="var(--grn)" onRemove={() => setIncomeSrcs((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                {income > 0 && !state.monthlyIncome && !incomeSrcs.length && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>💰 Income</div>
                    <ItemChip label={`${fc(income)}/mo take-home`} sub="Will set as your monthly income" col="var(--grn)" tag="new" tagCol="var(--grn)" />
                  </div>
                )}
                {!hasChanges && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--mut)', fontSize: 13 }}>
                    No changes detected — everything looks the same.
                  </div>
                )}
              </>
            ) : (
              // Setup mode: existing removable chips
              <>
                {bills.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>📋 Bills ({bills.length})</div>
                    {bills.map((b, i) => (
                      <ItemChip key={b._id} label={b.name}
                        sub={`${fc(b.amt)} · due ${ordinal(b.due || 1)} of the month`}
                        col={b.col} onRemove={() => setBills((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                {subs.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>🔄 Subscriptions ({subs.length})</div>
                    {subs.map((b, i) => (
                      <ItemChip key={b._id} label={b.name}
                        sub={`${fc(b.amt)} · ${b.cycle === 'annual' ? 'annual' : 'monthly'} · due ${ordinal(b.due || 1)}`}
                        col={b.col} onRemove={() => setSubs((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                {cards.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>💳 Cards ({cards.length})</div>
                    {cards.map((c, i) => (
                      <ItemChip key={c._id} label={c.name}
                        sub={`${fc(c.bal)} balance · ${c.apr > 0 ? c.apr + '% APR' : 'APR unknown'} · ${c.min > 0 ? fc(c.min) + ' min' : 'min unknown'}`}
                        col={c.col || '#ffd93d'} onRemove={() => setCards((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                {incomeSrcs.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>💰 Paychecks</div>
                    {incomeSrcs.map((x, i) => (
                      <ItemChip key={x.id} label={`${x.name}: ${fc(x.amt)} take-home`}
                        sub={`${FREQ_LABELS[x.freq] || x.freq}${x.firstPayMs ? ' · paid ' + new Date(x.firstPayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} · ${fc(sourcesToMonthly([x]))}/mo`}
                        col="var(--grn)" tag="new" tagCol="var(--grn)" onRemove={() => setIncomeSrcs((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                {income > 0 && !incomeSrcs.length && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>💰 Income</div>
                    <ItemChip label={`${fc(income)}/mo take-home`}
                      sub={state.monthlyIncome ? 'Note: you already have income set — this will be skipped' : 'Will set as your monthly income'}
                      col="var(--grn)" onRemove={() => setIncome(0)} />
                  </div>
                )}
                {!hasChanges && (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--mut)', fontSize: 13 }}>
                    Everything removed — nothing to apply.
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={() => { setStep('input'); setError(null); }}
                style={{ flex: 1, padding: '11px', borderRadius: 11, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                ← Try again
              </button>
              <button
                onClick={handleApply}
                disabled={!hasChanges}
                style={{
                  flex: 2, padding: '11px', borderRadius: 11,
                  background: hasChanges ? 'var(--ylw)' : 'var(--surf2)',
                  color: hasChanges ? '#000' : 'var(--mut)',
                  fontWeight: 800, fontSize: 13, border: 'none',
                  cursor: hasChanges ? 'pointer' : 'default', fontFamily: 'inherit',
                }}
              >
                {isEdit ? 'Apply changes →' : 'Apply to my dashboard →'}
              </button>
            </div>
          </>
        )}

        {/* ── Done step: first look at the plan ── */}
        {step === 'done' && summary && (
          <>
            {[
              ['💰 Coming in each month', fc(summary.monthly), 'var(--grn)'],
              ['📋 Bills and subscriptions', '−' + fc(summary.billsMo), 'var(--txt)'],
              ...(summary.minsMo > 0 ? [['💳 Card minimums', '−' + fc(summary.minsMo), 'var(--txt)']] : []),
            ].map(([l, v, c]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 2px', borderBottom: '1px solid var(--bdr)', fontSize: 13 }}>
                <span style={{ color: 'var(--mut)' }}>{l}</span><strong style={{ color: c }}>{v}</strong>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 2px', fontSize: 15 }}>
              <strong>Left for spending and goals</strong>
              <strong style={{ color: summary.left >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fc(summary.left)}/mo</strong>
            </div>
            {summary.left < 0 && (
              <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
                Your bills are more than your income. Double-check the amounts in Plan, or add any income that's missing.
              </div>
            )}
            {summary.payoff && summary.payoff.end !== '—' && (
              <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(107,203,119,.08)', border: '1px solid rgba(107,203,119,.25)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
                🎯 Putting {fc(Math.max(0, Math.round(summary.left * 0.2)))}/mo extra toward your {fc(summary.debt)} of card debt makes you <strong>debt-free by {summary.payoff.end}</strong>.
              </div>
            )}
            <button
              onClick={onClose}
              style={{ width: '100%', padding: '13px', borderRadius: 12, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              See my dashboard →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
